import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle, CheckCircle, Shield, Gavel, FileText, Send } from "lucide-react";
import { toast } from "sonner";
import { BOARD_STANCES, PROJECTS, PROJECT_CATEGORIES, PROJECT_STATUS_COLORS } from "../config/wiseboardConfig";
import type { BoardStance, ProjectStatus } from "../config/wiseboardConfig";

interface BoardDecisionFormProps {
  caseId: string;
  dbProject: any;
  artifacts: any[];
  onArtifactSaved: () => void;
}

interface DecisionPayload {
  stance: BoardStance;
  mecePoints: [string, string, string];
  dissent: string;
  weakestLink: string;
  conditionsPrecedent: string;
  criticalDecisionSensitivity: boolean;
  sensitivityReason: string;
}

const STANCE_CONFIG: Record<BoardStance, { icon: any; color: string; description: string; gatesP22: boolean }> = {
  "Go": { icon: CheckCircle, color: "text-green-600", description: "Proceed — evidence supports the strategy", gatesP22: true },
  "Conditional-Go": { icon: Shield, color: "text-amber-600", description: "Proceed if stated conditions are met", gatesP22: true },
  "Pilot": { icon: Gavel, color: "text-blue-600", description: "Limited deployment to gather more evidence", gatesP22: true },
  "No-Go": { icon: AlertTriangle, color: "text-red-600", description: "Do not proceed — evidence insufficient or negative", gatesP22: false },
};

const EMPTY_DECISION: DecisionPayload = {
  stance: "Go",
  mecePoints: ["", "", ""],
  dissent: "",
  weakestLink: "",
  conditionsPrecedent: "",
  criticalDecisionSensitivity: false,
  sensitivityReason: "",
};

export default function BoardDecisionForm({ caseId, dbProject, artifacts, onArtifactSaved }: BoardDecisionFormProps) {
  const [decision, setDecision] = useState<DecisionPayload>(EMPTY_DECISION);
  const [saving, setSaving] = useState(false);
  const [p22Project, setP22Project] = useState<any>(null);

  // Load existing decision from latest artifact
  useEffect(() => {
    if (artifacts.length > 0) {
      try {
        const parsed = JSON.parse(artifacts[0].content || "{}");
        if (parsed.stance) setDecision(parsed);
      } catch { /* not JSON, ignore */ }
    }
  }, [artifacts]);

  // Load P22 project status
  useEffect(() => {
    const loadP22 = async () => {
      const { data } = await supabase
        .from("wiseboard_projects")
        .select("id, status")
        .eq("case_id", caseId)
        .eq("project_type", "P22")
        .single();
      if (data) setP22Project(data);
    };
    loadP22();
  }, [caseId]);

  const stanceConfig = STANCE_CONFIG[decision.stance];
  const meceComplete = decision.mecePoints.every(p => p.trim().length > 0);
  const formValid = meceComplete && decision.weakestLink.trim().length > 0;

  const handleSave = async () => {
    if (!formValid) { toast.error("Complete all 3 MECE points and identify the weakest link"); return; }
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Not authenticated"); setSaving(false); return; }

    const latestVersion = artifacts.length > 0 ? Math.max(...artifacts.map((a: any) => a.version)) : 0;
    const content = JSON.stringify(decision, null, 2);

    const { error } = await supabase.from("wiseboard_artifacts").insert({
      project_id: dbProject.id,
      user_id: user.id,
      name: "P21 — Board Decision",
      version: latestVersion + 1,
      content,
      approval_status: "draft",
    });

    if (error) { toast.error(error.message); setSaving(false); return; }

    // Auto-gate P22: if stance is positive, ensure P22 can proceed; if No-Go, hold P22
    if (p22Project) {
      if (!stanceConfig.gatesP22 && p22Project.status !== "hold") {
        await supabase.from("wiseboard_projects").update({ status: "hold" }).eq("id", p22Project.id);
        setP22Project({ ...p22Project, status: "hold" });
        toast.info("P22 Sales Activation set to HOLD (No-Go stance)");
      } else if (stanceConfig.gatesP22 && p22Project.status === "hold") {
        await supabase.from("wiseboard_projects").update({ status: "not_started" }).eq("id", p22Project.id);
        setP22Project({ ...p22Project, status: "not_started" });
        toast.info("P22 Sales Activation unblocked");
      }
    }

    toast.success(`Board Decision v${latestVersion + 1} saved`);
    setSaving(false);
    onArtifactSaved();
  };

  const updateMece = (index: number, value: string) => {
    const updated = [...decision.mecePoints] as [string, string, string];
    updated[index] = value;
    setDecision(d => ({ ...d, mecePoints: updated }));
  };

  return (
    <div className="space-y-4">
      {/* Stance Selection */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Gavel className="h-4 w-4" /> Board Stance
          </CardTitle>
          <CardDescription className="text-xs">Select the final recommendation stance</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={decision.stance}
            onValueChange={(v) => setDecision(d => ({ ...d, stance: v as BoardStance }))}
            className="grid grid-cols-2 gap-3"
          >
            {BOARD_STANCES.map(stance => {
              const cfg = STANCE_CONFIG[stance];
              const Icon = cfg.icon;
              const selected = decision.stance === stance;
              return (
                <label
                  key={stance}
                  className={`flex items-start gap-3 rounded-lg border-2 p-3 cursor-pointer transition-all ${
                    selected ? "border-primary bg-primary/5" : "border-muted hover:border-muted-foreground/30"
                  }`}
                >
                  <RadioGroupItem value={stance} className="mt-0.5" />
                  <div>
                    <div className={`font-semibold text-sm flex items-center gap-1.5 ${cfg.color}`}>
                      <Icon className="h-4 w-4" /> {stance}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{cfg.description}</p>
                    {!cfg.gatesP22 && (
                      <Badge variant="destructive" className="text-[9px] mt-1">Blocks P22</Badge>
                    )}
                  </div>
                </label>
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* 3 MECE Evidence Points */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">3 MECE Evidence Points *</CardTitle>
          <CardDescription className="text-xs">Mutually Exclusive, Collectively Exhaustive — the three pillars supporting the stance</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[0, 1, 2].map(i => (
            <div key={i}>
              <Label className="text-xs font-mono">Point {i + 1} {decision.mecePoints[i].trim() ? "✓" : ""}</Label>
              <Textarea
                rows={2}
                className="text-sm mt-1"
                placeholder={`Evidence point ${i + 1} — state the claim, source, and confidence…`}
                value={decision.mecePoints[i]}
                onChange={e => updateMece(i, e.target.value)}
              />
            </div>
          ))}
          {!meceComplete && (
            <p className="text-[11px] text-destructive flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> All 3 MECE points required before saving
            </p>
          )}
        </CardContent>
      </Card>

      {/* Dissent & Weakest Link */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Dissent</CardTitle>
            <CardDescription className="text-xs">Counter-arguments or minority views</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              rows={3}
              className="text-sm"
              placeholder="Record any dissenting views, counter-evidence, or minority positions…"
              value={decision.dissent}
              onChange={e => setDecision(d => ({ ...d, dissent: e.target.value }))}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Weakest Link *</CardTitle>
            <CardDescription className="text-xs">The single most vulnerable element in the case</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              rows={3}
              className="text-sm"
              placeholder="Identify the weakest link in the evidence chain…"
              value={decision.weakestLink}
              onChange={e => setDecision(d => ({ ...d, weakestLink: e.target.value }))}
            />
          </CardContent>
        </Card>
      </div>

      {/* Conditions Precedent */}
      {(decision.stance === "Conditional-Go" || decision.stance === "Pilot") && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Conditions Precedent</CardTitle>
            <CardDescription className="text-xs">What must be true before proceeding</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              rows={3}
              className="text-sm"
              placeholder="List conditions that must be satisfied…"
              value={decision.conditionsPrecedent}
              onChange={e => setDecision(d => ({ ...d, conditionsPrecedent: e.target.value }))}
            />
          </CardContent>
        </Card>
      )}

      {/* Critical Decision Sensitivity */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Critical Decision Sensitivity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={decision.criticalDecisionSensitivity}
              onCheckedChange={v => setDecision(d => ({ ...d, criticalDecisionSensitivity: v }))}
            />
            <span className="text-sm">Flag this decision as high-sensitivity</span>
          </div>
          {decision.criticalDecisionSensitivity && (
            <Textarea
              rows={2}
              className="text-sm"
              placeholder="Explain why this decision is critically sensitive…"
              value={decision.sensitivityReason}
              onChange={e => setDecision(d => ({ ...d, sensitivityReason: e.target.value }))}
            />
          )}
        </CardContent>
      </Card>

      {/* P22 Gate Status */}
      <Card className={`border-2 ${stanceConfig.gatesP22 ? "border-green-300" : "border-red-300"}`}>
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              {stanceConfig.gatesP22 ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-600" />
              )}
              <span>
                P22 Sales Activation: {stanceConfig.gatesP22 ? (
                  <span className="font-semibold text-green-700">Unblocked</span>
                ) : (
                  <span className="font-semibold text-red-700">Blocked (No-Go)</span>
                )}
              </span>
            </div>
            {p22Project && (
              <Badge className={PROJECT_STATUS_COLORS[(p22Project.status || "not_started") as ProjectStatus]}>
                {p22Project.status?.replace("_", " ")}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <Button className="w-full" size="lg" onClick={handleSave} disabled={saving || !formValid}>
        <FileText className="h-4 w-4 mr-2" />
        {saving ? "Saving…" : "Save Board Decision"}
      </Button>
    </div>
  );
}
